import React, { useEffect, useState, useRef, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { View, Text, Image, ScrollView, StyleSheet, ActivityIndicator, TouchableOpacity, Platform } from 'react-native';
import { useParams, useNavigate } from 'react-router-dom';
import { MdLocationOn, MdChevronLeft, MdChevronRight, MdClose } from 'react-icons/md';
import { supabase } from '@cubemeet/core/services/supabase';
import ShareButton from '@cubemeet/ui/components/common/ShareButton';
import { useGlobalToast } from '../contexts/ToastContext';
import { Breadcrumbs, getTravelBackgroundAssetNoFallback } from '@cubemeet/ui';
import { buildPostBreadcrumb } from '@cubemeet/module-travel/web/utils/breadcrumbBuilders';
import useShowBackButton from '../shared/hooks/useShowBackButton';
import RichContentRenderer from '@cubemeet/module-travel/web/components/RichContentRenderer';
import SnapshotContentRenderer from '@cubemeet/module-travel/web/components/SnapshotContentRenderer';
import PostEngagement from '../components/PostEngagement';
import { cloneTravelPostToItinerary } from '@cubemeet/module-travel/shared/travelPostService';
import CloneItineraryModal from './components/CloneItineraryModal';
import AddToItineraryModal from '../components/AddToItineraryModal';
import { useTravelI18n } from '../modules/travel/useTravelI18n';
import { checkIsUserLiked, likeUser, unlikeUser } from '@cubemeet/core/services/userProfileService';
import { RouteMapSheet } from '@cubemeet/module-travel';
import { buildLatLng } from '@cubemeet/module-travel/web/utils/coordinateHelpers';

/**
 * TravelPostDetailPage - 旅行帖子详情页
 * 路由：/travel-post/:id
 */
const TravelPostDetailPage: React.FC = () => {
  const { t, language } = useTravelI18n();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  // TravelPostDetailPage is public — do not force redirect to auth on mount.
  // 全局吐司（Hook 必须在组件顶层调用）
  const { showSuccess, showError, showInfo } = useGlobalToast();
  const [post, setPost] = useState<any>(() => {
    try {
      if (typeof window !== 'undefined' && (window as any).__INITIAL_POST__) {
        const p = (window as any).__INITIAL_POST__;
        try { delete (window as any).__INITIAL_POST__; } catch (e) {}
        return p;
      }
    } catch (e) {
      // ignore
    }
    return null;
  });
  const [author, setAuthor] = useState<any>(null);
  const [isFollowing, setIsFollowing] = useState<boolean>(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actualCommentCount, setActualCommentCount] = useState(0);
  const [liked, setLiked] = useState(false);
  const [isAuthorLiked, setIsAuthorLiked] = useState(false);
  const [breadcrumbItems, setBreadcrumbItems] = useState<any[] | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  // Hook: 是否显示页面顶部的返回按钮（web 内部来源）
  const showBack = useShowBackButton();
  const [cloning, setCloning] = useState(false);
  const [showCloneModal, setShowCloneModal] = useState(false);
  const [showAddToItineraryModal, setShowAddToItineraryModal] = useState(false);
  const [externalSharePayload, setExternalSharePayload] = useState<{
    title?: string;
    text?: string;
    url?: string;
    imageUrl?: string;
    rawPayload?: any;
  } | null>(null);

  // Memoize content data to prevent unnecessary re-renders
  const contentData = useMemo(() => {
    if (!post?.content_json) return null;
    try {
      return typeof post.content_json === 'string' 
        ? JSON.parse(post.content_json) 
        : post.content_json;
    } catch (e) {
      console.warn('Failed to parse post content:', e);
      return null;
    }
  }, [post?.content_json]);

  const isSnapshot = useMemo(() => {
    return contentData?.days && Array.isArray(contentData.days);
  }, [contentData]);

  const handleAddToItinerary = React.useCallback((entryPayload: any) => {
    // 檢查是否登錄
    supabase.auth.getUser().then(({ data: { user } }) => {
      if (!user) {
        const ref = typeof window !== 'undefined' ? encodeURIComponent(window.location.pathname + window.location.search) : '/';
        navigate(`/auth?referer=${ref}`);
        return;
      }
      // 設定 external share payload 並打開 modal
      // 傳遞完整的 rawPayload 以保留地址、電話等資訊
      setExternalSharePayload({
        title: entryPayload.title,
        text: entryPayload.description,
        url: entryPayload.url,
        imageUrl: entryPayload.imageUrl,
        rawPayload: entryPayload.payload, // 完整的原始 payload
      });
      setShowAddToItineraryModal(true);
    });
  }, [navigate]);

  // Map Sheet State
  const [showRouteMap, setShowRouteMap] = useState(false);
  const [sheetHeight, setSheetHeight] = useState(70); // 70% default
  const [isDragging, setIsDragging] = useState(false);
  const dragStartY = useRef<number>(0);
  const dragStartHeight = useRef<number>(0);

  // Lightbox State (Lifted up to prevent auto-close on re-render)
  const [lightboxState, setLightboxState] = useState<{
    images: any[];
    currentIndex: number;
  } | null>(null);

  const handleOpenLightbox = React.useCallback((images: any[], startIndex: number) => {
    setLightboxState({ images, currentIndex: startIndex });
  }, []);

  const closeLightbox = React.useCallback(() => {
    setLightboxState(null);
  }, []);

  const nextImage = React.useCallback(() => {
    setLightboxState(prev => {
      if (!prev) return null;
      return {
        ...prev,
        currentIndex: (prev.currentIndex + 1) % prev.images.length
      };
    });
  }, []);

  const prevImage = React.useCallback(() => {
    setLightboxState(prev => {
      if (!prev) return null;
      return {
        ...prev,
        currentIndex: (prev.currentIndex - 1 + prev.images.length) % prev.images.length
      };
    });
  }, []);

  // Hide bottom nav when lightbox is open to ensure full screen
  useEffect(() => {
    if (lightboxState && typeof document !== 'undefined') {
      const portal = document.getElementById('app-bottom-nav-portal');
      if (portal) portal.style.setProperty('display', 'none', 'important');
      
      const elements = document.getElementsByClassName('app-bottom-nav-root');
      for (let i = 0; i < elements.length; i++) {
        (elements[i] as HTMLElement).style.setProperty('display', 'none', 'important');
      }

      return () => {
        if (portal) portal.style.removeProperty('display');
        for (let i = 0; i < elements.length; i++) {
          (elements[i] as HTMLElement).style.removeProperty('display');
        }
      };
    }
  }, [!!lightboxState]);

  // Calculate route spots from post content
  const routeSpots = useMemo(() => {
    if (!post?.content_json) return [];
    try {
      const contentData = typeof post.content_json === 'string' 
        ? JSON.parse(post.content_json) 
        : post.content_json;
      
      if (!contentData?.days || !Array.isArray(contentData.days)) return [];
      
      const spots: any[] = [];
      let globalIndex = 0;

      contentData.days.forEach((day: any) => {
        if (day.entries && Array.isArray(day.entries)) {
          day.entries.forEach((entry: any) => {
            // Try multiple sources for location data
            const sources = [
              entry.payload,
              entry.custom_payload,
              entry.inline_payload,
              entry.card,
              entry.meta,
              entry
            ];
            
            let latlng: [number, number] | null = null;
            let usedSrc: any = null;

            for (const s of sources) {
              if (!s) continue;
              latlng = buildLatLng(s);
              if (latlng) {
                usedSrc = s;
                break;
              }
            }
            
            if (latlng) {
              const name = entry.title || usedSrc.title || usedSrc.name || `景点 ${globalIndex + 1}`;
              const imageUrl = usedSrc.imageUrl || usedSrc.image || usedSrc.cover_image || usedSrc.thumbnail || null;
              const address = usedSrc.address || usedSrc.formatted_address || null;
              
              spots.push({
                id: String(entry.id || globalIndex),
                name,
                lat: latlng[0],
                lng: latlng[1],
                imageUrl,
                address
              });
              globalIndex++;
            }
          });
        }
      });
      
      return spots;
    } catch (e) {
      console.warn('Failed to parse route spots:', e);
      return [];
    }
  }, [post?.content_json]);

  // Drag handlers
  const handleSheetPointerDown = (e: React.PointerEvent | React.TouchEvent) => {
    const clientY = 'touches' in e ? e.touches[0].clientY : (e as React.PointerEvent).clientY;
    dragStartY.current = clientY;
    dragStartHeight.current = sheetHeight;
    setIsDragging(true);
  };

  const handleSheetPointerMove = (e: PointerEvent | TouchEvent) => {
    if (!isDragging) return;
    const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;
    const deltaY = dragStartY.current - clientY;
    const viewportHeight = window.innerHeight;
    const deltaPercent = (deltaY / viewportHeight) * 100;
    let newHeight = dragStartHeight.current + deltaPercent;
    newHeight = Math.max(30, Math.min(90, newHeight));
    setSheetHeight(newHeight);
  };

  const handleSheetPointerUp = () => {
    if (!isDragging) return;
    setIsDragging(false);
    if (sheetHeight < 40) {
      setShowRouteMap(false);
    } else if (sheetHeight > 80) {
      setSheetHeight(90);
    } else {
      setSheetHeight(70);
    }
  };

  useEffect(() => {
    if (isDragging) {
      const move = (e: any) => handleSheetPointerMove(e);
      const up = () => handleSheetPointerUp();
      window.addEventListener('pointermove', move);
      window.addEventListener('pointerup', up);
      window.addEventListener('touchmove', move);
      window.addEventListener('touchend', up);
      return () => {
        window.removeEventListener('pointermove', move);
        window.removeEventListener('pointerup', up);
        window.removeEventListener('touchmove', move);
        window.removeEventListener('touchend', up);
      };
    }
  }, [isDragging, sheetHeight]);

  // 判斷是否為「真實行程」（非評測/假行程）
  // 假行程特徵：所有 entries 都是 external 類型且沒有 card_id
  const isRealItinerary = useMemo(() => {
    if (!post?.content_json) return false;
    try {
      const contentData = typeof post.content_json === 'string' 
        ? JSON.parse(post.content_json) 
        : post.content_json;
      
      if (!contentData?.days || !Array.isArray(contentData.days)) return false;
      
      // 收集所有 entries
      const allEntries: any[] = [];
      for (const day of contentData.days) {
        if (day.entries && Array.isArray(day.entries)) {
          allEntries.push(...day.entries);
        }
      }
      
      if (allEntries.length === 0) return false;
      
      // 計算「真實」的 entry 數量
      const realEntryCount = allEntries.filter(entry => {
        // 有 card_id 表示關聯了真實卡片
        if (entry.meta?.card_id || entry.meta?.cardId || entry.card?.id || entry.card_id) {
          return true;
        }
        // 如果是 transport 類型也算真實
        if (entry.type === 'transport') {
          return true;
        }
        // 如果 payload.type 不是 external，也算真實
        if (entry.payload?.type && entry.payload.type !== 'external') {
          return true;
        }
        return false;
      }).length;
      
      // 只有當真實節點超過 1 個時，才視為可複製的行程
      // (避免單個地點的評測文章顯示此按鈕，因為那通常只是為了關聯地點)
      return realEntryCount > 1;
    } catch {
      return false;
    }
  }, [post?.content_json]);

  // Back to Top logic
  const scrollViewRef = useRef<ScrollView>(null);
  const [showBackToTop, setShowBackToTop] = useState(false);
  const [showStickyHeader, setShowStickyHeader] = useState(false);

  // Web window scroll listener
  useEffect(() => {
    if (Platform.OS !== 'web') return;

    const handleWebScroll = () => {
      const offsetY = window.scrollY;
      // Simply show if scrolled down, hide if at top. 
      // Removed auto-hide timer to prevent flickering and unnecessary re-renders.
      setShowBackToTop(offsetY > 300);
      // Show sticky header after scrolling past the cover image (~200px)
      setShowStickyHeader(offsetY > 200);
    };

    window.addEventListener('scroll', handleWebScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleWebScroll);
    };
  }, []);

  // Sticky header layout: constrain to content width (600px) on wide screens
  const [stickyLayoutStyle, setStickyLayoutStyle] = useState<any>({ left: 0, right: 0 });

  useEffect(() => {
    if (Platform.OS !== 'web') return;

    const calc = () => {
      try {
        const w = window.innerWidth || 0;
        // If viewport wider than 640, center header and limit width to 600
        if (w > 640) {
          setStickyLayoutStyle({ left: '50%', width: 600, marginLeft: -300 });
        } else {
          setStickyLayoutStyle({ left: 0, right: 0, width: '100%', marginLeft: 0 });
        }
      } catch (e) {
        setStickyLayoutStyle({ left: 0, right: 0 });
      }
    };

    calc();
    window.addEventListener('resize', calc);
    return () => window.removeEventListener('resize', calc);
  }, []);

  // Mobile ScrollView listener
  const handleScroll = (event: any) => {
    if (Platform.OS === 'web') return; // Ignore on web, use window listener

    const offsetY = event.nativeEvent.contentOffset.y;
    setShowBackToTop(offsetY > 300);
  };

  const scrollToTop = () => {
    if (Platform.OS === 'web') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      scrollViewRef.current?.scrollTo({ y: 0, animated: true });
    }
  };

  const handleClone = async () => {
    if (!post || !id) return;
    
    // Check auth
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      // redirect to login
      const ref = typeof window !== 'undefined' ? encodeURIComponent(window.location.pathname + window.location.search) : '/';
      navigate(`/auth?referer=${ref}`);
      return;
    }
  
    setShowCloneModal(true);
  };

  const handleConfirmClone = async (title: string, startDateStr: string) => {
    if (!post || !id) return;
    
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    setCloning(true);
    setShowCloneModal(false);

    try {
      const startDate = startDateStr ? new Date(startDateStr) : undefined;
      const result = await cloneTravelPostToItinerary(id, user.id, supabase, {
        title,
        startDate,
      });

      if (result.success && result.groupId) {
        showSuccess && showSuccess(t('travelPostDetail.messages.cloneSuccess'));
        // Navigate to the itinerary editor using the hash-based routing of TravelModule
        // The TravelModule is mounted at /travel/* and listens to window.location.hash
        // Format: /travel#travel?view=editor&groupId={groupId}
        navigate(`/travel#travel?view=editor&groupId=${result.groupId}`);
      } else {
        throw result.error || new Error(t('travelPostDetail.messages.cloneError'));
      }
    } catch (e) {
      console.error(e);
      showError && showError(t('travelPostDetail.messages.cloneError'));
    } finally {
      setCloning(false);
    }
  };

  const handleAuthorLikePress = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        const ref = typeof window !== 'undefined' ? encodeURIComponent(window.location.pathname + window.location.search) : '/';
        navigate(`/auth?referer=${ref}`);
        return;
      }
      
      if (!author?.id) return;

      if (isAuthorLiked) {
        await unlikeUser(user.id, author.id);
        setIsAuthorLiked(false);
        showSuccess && showSuccess('已取消點讚');
      } else {
        await likeUser(user.id, author.id);
        setIsAuthorLiked(true);
        showSuccess && showSuccess('已點讚');
      }
    } catch (e: any) {
      console.error('Author like error:', e);
      showError && showError(e.message || '操作失败');
    }
  };

  const handleLike = async () => {
    if (!post || !id) return;
    
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      const ref = typeof window !== 'undefined' ? encodeURIComponent(window.location.pathname + window.location.search) : '/';
      navigate(`/auth?referer=${ref}`);
      return;
    }

    const newLiked = !liked;
    setLiked(newLiked);
    
    // Optimistic update for UI
    setPost((prev: any) => ({
      ...prev,
      like_count: (prev.like_count || 0) + (newLiked ? 1 : -1)
    }));

    try {
      if (newLiked) {
        // Try insert like
        const { error } = await supabase.from('travel_post_likes').insert({
          post_id: id,
          user_id: user.id
        });
        
        if (error) throw error;

        // Increment count via RPC or fallback
        const { error: rpcError } = await supabase.rpc('increment_post_like', { post_id: id });
        if (rpcError) {
          // Fallback to direct update if RPC fails (note: this may fail due to RLS if not author)
          await supabase
            .from('travel_posts')
            .update({ like_count: (post.like_count || 0) + 1 })
            .eq('id', id);
        }
      } else {
        // Try delete like
        const { error } = await supabase.from('travel_post_likes').delete()
          .eq('post_id', id)
          .eq('user_id', user.id);
          
        if (error) throw error;

        // Decrement count via RPC or fallback
        const { error: rpcError } = await supabase.rpc('decrement_post_like', { post_id: id });
        if (rpcError) {
           await supabase
            .from('travel_posts')
            .update({ like_count: Math.max(0, (post.like_count || 1) - 1) })
            .eq('id', id);
        }
      }
    } catch (e: any) {
      console.warn('Like operation failed (likely missing table), falling back to local storage:', e);
      
      // Fallback: Update count directly on travel_posts
      const newCount = Math.max(0, (post.like_count || 0) + (newLiked ? 1 : -1));
      const { error: updateError } = await supabase
        .from('travel_posts')
        .update({ like_count: newCount })
        .eq('id', id);

      if (updateError) {
        console.warn('Fallback update failed (likely RLS), keeping local state:', updateError);
        // Do NOT revert state. Just persist locally.
      }
      
      // Always persist to localStorage in fallback/error cases to ensure UI consistency
      try {
        const key = `cm_liked_posts_${user.id}`;
        const stored = JSON.parse(localStorage.getItem(key) || '[]');
        if (newLiked) {
          if (!stored.includes(id)) stored.push(id);
        } else {
          const idx = stored.indexOf(id);
          if (idx > -1) stored.splice(idx, 1);
        }
        localStorage.setItem(key, JSON.stringify(stored));
      } catch (err) {
        // ignore storage error
      }
    }
  };

  const handleCommentAdded = async (content: string) => {
    // Update local count
    setActualCommentCount(prev => prev + 1);
    
    // Update DB count
    try {
      const { error: rpcError } = await supabase.rpc('increment_post_comment', { post_id: id });
      if (rpcError) {
        // Fallback
        await supabase
          .from('travel_posts')
          .update({ comment_count: (actualCommentCount || post.comment_count || 0) + 1 })
          .eq('id', id);
      }
    } catch (e) {
      console.warn('Failed to update comment count in DB:', e);
    }
  };

  useEffect(() => {
    if (!id) {
      setError(t('travelPostDetail.error.missingId'));
      setLoading(false);
      return;
    }

    let mounted = true;

    // Use a retrying fetch to handle transient failures (network, service worker cache race, etc.)
    let retryTimer: any = null;
    let cancelled = false;
    const attemptFetch = async (retriesLeft: number) => {
      try {
        if (!mounted || cancelled) return;

        // If we already have an initial post injected by worker, only skip fetching
        // when it already contains the full content (content_json). Workers
        // inject a minimal payload (id/title/cover/summary) for faster first
        // paint — in that case we must still fetch the full post to obtain
        // `content_json` (otherwise the page shows "暂无内容").
        if (post && post.content_json) {
          setLoading(false);
          return;
        }

        console.log(`[TravelPostDetailPage] fetching post ${id}, retriesLeft=${retriesLeft}`);

        // 1. 获取帖子数据
        const { data: postData, error: postError } = await supabase
          .from('travel_posts')
          .select('*')
          .eq('id', id)
          .single();

        if (!mounted || cancelled) return;

        if (postError || !postData) {
          const msg = postError ? String(postError.message || postError) : '帖子不存在或已被删除';
          console.warn('[TravelPostDetailPage] fetch error:', msg);
          if (retriesLeft > 0) {
            // backoff a little and retry
            retryTimer = setTimeout(() => attemptFetch(retriesLeft - 1), 700);
            return;
          }
          setError(t('travelPostDetail.error.notFound'));
          setLoading(false);
          return;
        }

        setPost(postData);

        // 2. 获取作者信息
        if (postData.author_id) {
          try {
            const { data: authorData } = await supabase
              .from('profiles')
              .select('id, display_name, avatar_url, bio')
              .eq('id', postData.author_id)
              .single();

            if (!mounted || cancelled) return;
            setAuthor(authorData);
            
            // Check if current user likes the author
            try {
              const { data: { user } } = await supabase.auth.getUser();
              if (user) {
                 const authorLiked = await checkIsUserLiked(user.id, postData.author_id);
                 if (mounted && !cancelled) setIsAuthorLiked(authorLiked);
              }
            } catch (e) { console.warn('Failed to check author like status:', e); }

            // Note: follow status will be checked lazily on user action
          } catch (e) {
            console.warn('Failed to load author info:', e);
          }
        }

        // 3. 更新浏览次数（don't block rendering on this)
        try {
          // Use RPC to bypass RLS restrictions for non-authors
          await supabase.rpc('increment_post_view', { post_id: id });
        } catch (e) {
          console.warn('Failed to update view_count:', e);
        }

        // 4. 检查点赞状态
        try {
          const { data: { user } } = await supabase.auth.getUser();
          if (user) {
            const { data: likeData, error } = await supabase
              .from('travel_post_likes')
              .select('*')
              .eq('post_id', id)
              .eq('user_id', user.id)
              .maybeSingle();
            
            let isLiked = false;
            if (!error && likeData) {
              isLiked = true;
            }
            
            // Always check localStorage to merge state (in case DB write failed previously)
            try {
               const key = `cm_liked_posts_${user.id}`;
               const stored = JSON.parse(localStorage.getItem(key) || '[]');
               if (stored.includes(id)) {
                 isLiked = true;
                 // If liked locally but not in DB (or DB count is 0), we might need to adjust count
                 if (!likeData) {
                    setPost((p: any) => ({ ...p, like_count: (p.like_count || 0) + 1 }));
                 }
               }
            } catch (e) {}
            
            setLiked(isLiked);
          }
        } catch (e) {
          console.warn('Failed to check like status:', e);
        }

        if (mounted && !cancelled) setLoading(false);
      } catch (err) {
        console.error('[TravelPostDetailPage] Unexpected fetch error:', err);
        if (retriesLeft > 0) {
          retryTimer = setTimeout(() => attemptFetch(retriesLeft - 1), 700);
          return;
        }
        if (mounted && !cancelled) {
          setError(t('travelPostDetail.error.loadFailed'));
          setLoading(false);
        }
      }
    };

    attemptFetch(2);

    return () => {
      mounted = false;
      cancelled = true;
      if (retryTimer) clearTimeout(retryTimer);
    };
  }, [id, reloadKey]);

  // 动态注入 SEO 元信息（仅在 Web 环境下有效；提升 Google/Bing 等可执行 JS 的爬虫效果）
  useEffect(() => {
    if (typeof document === 'undefined' || !post) return;

    const title = `${post.title || t('travelPostDetail.meta.defaultTitle')} · CubeMeet`;
    const desc = post.summary || String(post.content_json?.intro || '') || '';
    const url = typeof window !== 'undefined' ? window.location.href : '';
    const image = post.cover_image || '/assets/images/brand-logo-hd.png';

    const ensureMeta = (selector: string, attrs: Record<string, string>) => {
      let el = document.head.querySelector(selector) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        Object.entries(attrs).forEach(([k, v]) => el!.setAttribute(k, v));
        document.head.appendChild(el!);
      } else {
        // update content only
        if (attrs['content']) el.setAttribute('content', attrs['content']);
      }
      return el;
    };

    // Title
    document.title = title;

    // Description
    ensureMeta('meta[name="description"]', { name: 'description', content: desc });

    // Canonical
    let linkCanonical = document.head.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!linkCanonical) {
      linkCanonical = document.createElement('link');
      linkCanonical.setAttribute('rel', 'canonical');
      document.head.appendChild(linkCanonical);
    }
    linkCanonical.setAttribute('href', url);

    // Open Graph
    ensureMeta('meta[property="og:title"]', { property: 'og:title', content: post.title || '' } as any);
    ensureMeta('meta[property="og:description"]', { property: 'og:description', content: desc } as any);
    ensureMeta('meta[property="og:type"]', { property: 'og:type', content: 'article' } as any);
    ensureMeta('meta[property="og:url"]', { property: 'og:url', content: url } as any);
    ensureMeta('meta[property="og:image"]', { property: 'og:image', content: image } as any);
    ensureMeta('meta[property="og:site_name"]', { property: 'og:site_name', content: t('travelPostDetail.meta.siteName') } as any);

    // Twitter
    ensureMeta('meta[name="twitter:card"]', { name: 'twitter:card', content: 'summary_large_image' });
    ensureMeta('meta[name="twitter:title"]', { name: 'twitter:title', content: post.title || '' });
    ensureMeta('meta[name="twitter:description"]', { name: 'twitter:description', content: desc });
    ensureMeta('meta[name="twitter:image"]', { name: 'twitter:image', content: image });

    // JSON-LD: 文章 + 面包屑
    const ldArticle = {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: post.title,
      description: desc,
      image: [image],
      datePublished: post.created_at,
      dateModified: post.updated_at || post.created_at,
      author: author ? { '@type': 'Person', name: author.display_name } : undefined,
      mainEntityOfPage: url,
    };
    const breadcrumbItems = [
      { '@type': 'ListItem', position: 1, name: t('travelPostDetail.breadcrumbs.home'), item: '/' },
    ];
    if (post?.content_json?.city) {
      breadcrumbItems.push({ '@type': 'ListItem', position: 2, name: String(post.content_json.city), item: `/city/${String(post.content_json.city).split(',')[0]}` });
      breadcrumbItems.push({ '@type': 'ListItem', position: 3, name: t('travelPostDetail.breadcrumbs.guide'), item: '/travel_post/guide' });
      breadcrumbItems.push({ '@type': 'ListItem', position: 4, name: post.title, item: url });
    } else {
      breadcrumbItems.push({ '@type': 'ListItem', position: 2, name: post.title, item: url });
    }
    const ldBreadcrumb = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: breadcrumbItems,
    };

    const upsertJsonLd = (id: string, json: any) => {
      let el = document.getElementById(id) as HTMLScriptElement | null;
      if (!el) {
        el = document.createElement('script');
        el.type = 'application/ld+json';
        el.id = id;
        document.head.appendChild(el);
      }
      el.textContent = JSON.stringify(json);
    };

    upsertJsonLd('ld-article', ldArticle);
    upsertJsonLd('ld-breadcrumb', ldBreadcrumb);

  }, [post, author]);

  // Compute breadcrumb items and omit the post title from the breadcrumb UI
  // (we still keep the full title in JSON-LD / SEO metadata)
  useEffect(() => {
    if (!post) return;

    const buildItems = () => {
      const items = buildPostBreadcrumb(post, t) || [];
      try {
        // Remove the final item only if it's non-navigable (likely the post title)
        // Navigable items (like '指南') have a `to` property and should be kept.
        if (items.length > 0) {
          const last = items[items.length - 1] as any;
          if (!last || !last.to) {
            items.pop();
          }
        }

        // Keep truncation for the visible last label if needed
        if (typeof window !== 'undefined') {
          const w = window.innerWidth || 0;
          const maxChars = w < 720 ? 28 : 60;
          const lastIdx = items.length - 1;
          if (lastIdx >= 0 && items[lastIdx] && items[lastIdx].label) {
            const label = String(items[lastIdx].label);
            if (label.length > maxChars) {
              items[lastIdx] = { ...items[lastIdx], label: label.substring(0, maxChars - 1) + '…' };
            }
          }
        }
      } catch (e) {
        // fallback: silently ignore
      }
      return items;
    };

    // initial build
    // If the post has a city field, ensure a link to the city index (/city)
    // appears after the '首頁' item (i.e. 首页 > 城市 > ...)
    const initialItems = buildItems();
    if (post?.content_json?.city || post?.city) {
      // If there is already a '首頁' item, insert '城市' as the second item.
      if (initialItems.length > 0 && (String(initialItems[0].label) === '首頁' || String(initialItems[0].label) === t('travelPostDetail.breadcrumbs.home'))) {
        if (!initialItems[1] || initialItems[1]?.to !== '/city') {
          initialItems.splice(1, 0, { label: t('travelPostDetail.breadcrumbs.city'), to: '/city' });
        }
      } else {
        // fallback: if no '首頁', prepend '城市' so breadcrumb still makes sense
        if (!initialItems.length || initialItems[0]?.to !== '/city') {
          initialItems.unshift({ label: t('travelPostDetail.breadcrumbs.city'), to: '/city' });
        }
      }
    }
    setBreadcrumbItems(initialItems);

    // update on resize
    const onResize = () => {
      const items = buildItems();
      if (post?.content_json?.city || post?.city) {
        if (items.length > 0 && (String(items[0].label) === '首頁' || String(items[0].label) === t('travelPostDetail.breadcrumbs.home'))) {
          if (!items[1] || items[1]?.to !== '/city') items.splice(1, 0, { label: t('travelPostDetail.breadcrumbs.city'), to: '/city' });
        } else {
          if (!items.length || items[0]?.to !== '/city') items.unshift({ label: t('travelPostDetail.breadcrumbs.city'), to: '/city' });
        }
      }
      setBreadcrumbItems(items);
    };
    if (typeof window !== 'undefined') {
      window.addEventListener('resize', onResize);
    }
    return () => {
      if (typeof window !== 'undefined') window.removeEventListener('resize', onResize);
    };
  }, [post]);

  if (loading) {
    return (
      <>
        <View style={styles.centerContainer}>
          <ActivityIndicator size="large" color="#ff6b35" />
          <Text style={styles.loadingText}>{t('travelPostDetail.loading')}</Text>
        </View>
        {/* Ensure lightbox stays mounted even if loading state flickers */}
        {lightboxState && typeof document !== 'undefined' && ReactDOM.createPortal(
          <div 
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              width: '100vw',
              height: '100vh',
              backgroundColor: 'rgba(0, 0, 0, 0.95)',
              zIndex: 2147483647,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              touchAction: 'none',
              overscrollBehavior: 'contain',
            }}
            onClick={closeLightbox}
          >
            <img
              src={lightboxState.images[lightboxState.currentIndex].url}
              alt={lightboxState.images[lightboxState.currentIndex].alt || ''}
              style={{
                maxWidth: '90vw',
                maxHeight: '90vh',
                objectFit: 'contain',
              }}
              onClick={(e) => e.stopPropagation()}
            />
            {/* Controls omitted for brevity in loading state, but structure is preserved */}
          </div>,
          document.body
        )}
      </>
    );
  }

  if (error || !post) {
    return (
      <View style={styles.centerContainer}>
        <Text style={styles.errorText}>{error || t('travelPostDetail.error.postNotFound')}</Text>
        <View style={{ flexDirection: 'row', gap: 12 }}>
          <TouchableOpacity
            onPress={() => {
              setReloadKey(k => k + 1);
              setLoading(true);
              setError(null);
            }}
            style={{ paddingVertical: 8, paddingHorizontal: 12, backgroundColor: '#FF6B35', borderRadius: 6 }}
            accessibilityLabel={t('travelPostDetail.actions.retry')}
          >
            <Text style={{ color: '#fff', fontWeight: '700' }}>{t('travelPostDetail.actions.retry')}</Text>
          </TouchableOpacity>
          <Text
            style={styles.backLink}
            onPress={() => navigate(-1)}
          >
            {t('travelPostDetail.actions.back')}
          </Text>
        </View>
      </View>
    );
  }

  // 解析封面圖：優先作為本地資源處理，否則作為 URL
  const resolveCoverSource = () => {
    if (!post.cover_image) return undefined;
    
    // 先嘗試作為本地資源解析（如 assets/images/xxx.webp）
    const assetSource = getTravelBackgroundAssetNoFallback(post.cover_image);
    if (assetSource) return assetSource;
    
    // 否則作為 URL 處理
    return { uri: post.cover_image };
  };

  const coverSource = resolveCoverSource();

  return (
    <View style={styles.container}>
      {/* Sticky Header - 滾動後顯示 */}
      {showStickyHeader && (
        <View style={[styles.stickyHeader as any, stickyLayoutStyle]}>
          <TouchableOpacity
            onPress={() => {
              if (window.history && window.history.length > 1) window.history.back();
              else window.location.href = '/';
            }}
            style={styles.stickyBackBtn as any}
            accessibilityLabel={t('travelPostDetail.actions.back')}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
              <path d="M15 18L9 12L15 6" stroke="#1F2937" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </TouchableOpacity>
          <Text style={styles.stickyTitle} numberOfLines={1}>{post.title}</Text>
          <View style={{ width: 40 }} />
        </View>
      )}
      <ScrollView 
        ref={scrollViewRef}
        style={{ flex: 1 }} 
        showsVerticalScrollIndicator={false}
        onScroll={handleScroll}
        scrollEventThrottle={16}
      >
      <View style={styles.contentWrapper}>
      {/* 封面图 */}
      {coverSource && (
        <View style={styles.coverWrapper as any}>
          <Image
            source={coverSource}
            style={styles.coverImage}
            resizeMode="cover"
          />
          {/* 遮罩層 */}
          <View style={styles.coverOverlay as any} />
          {/* 標題顯示在封面圖上 */}
          <View style={styles.titleWrapper as any}>
            <Text style={styles.coverTitle}>{post.title}</Text>
          </View>
          {/* 顶部左上角返回箭头（仅 web 且判断为内部来源时显示） */}
          {typeof window !== 'undefined' && showBack && Platform.OS === 'web' && (
            <View style={styles.backWrap as any}>
              <TouchableOpacity
                onPress={() => {
                  if (window.history && window.history.length > 1) window.history.back();
                  else window.location.href = '/';
                }}
                accessibilityLabel={t('travelPostDetail.actions.back')}
                style={{ padding: 8, borderRadius: 20 } as any}
              >
                {/* Simple larger left chevron icon (no text) */}
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
                  <path d="M15 18L9 12L15 6" stroke="#ffffff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </TouchableOpacity>
            </View>
          )}
        </View>
      )}

      {/* 麵包屑導航 (不要顯示帖子標題) */}
      <Breadcrumbs
        items={breadcrumbItems || (post ? (() => {
          const arr = buildPostBreadcrumb(post, t) || [];
          // only remove the last item if it has no `to` (non-navigable title)
          if (arr.length > 0) {
            const last = arr[arr.length - 1] as any;
            if (!last || !last.to) arr.pop();
          }
          return arr;
        })() : [])}
        onNavigate={(to) => navigate(to)}
      />

      {/* ShareButton - 创建人信息 + 分享功能 */}
      {author && (
        <ShareButton
          type="post"
          id={id}
          title={post.title}
          description={post.summary}
          imageUrl={post.cover_image}
          creator={{
            id: author.id,
            display_name: author.display_name,
            avatar_url: author.avatar_url,
            bio: author.bio,
          }}
          showNativeShare={true}
          showCopyLink={true}
          onCreatorPress={() => navigate(`/user/${author.id}`)}
            userRelation={{ isFollowing, isLiked: isAuthorLiked }}
            onLikePress={handleAuthorLikePress}
            onFollowPress={async (targetId?: string) => {
              try {
                const { supabase } = await import('@cubemeet/core/services/supabase');
                const authRes: any = await supabase.auth.getUser();
                const currentUser = authRes?.data?.user || (supabase.auth && (supabase.auth.user && supabase.auth.user()));
                console.log('Follow attempt: currentUser=', currentUser && { id: currentUser.id, email: currentUser.email });
                console.log('Follow attempt: targetId=', targetId);
                if (!currentUser) {
                  const ref = typeof window !== 'undefined' ? encodeURIComponent(window.location.pathname + window.location.search) : '/';
                  navigate(`/auth?referer=${ref}`);
                  return;
                }

                if (!targetId) return;

                console.log('Calling supabase.from("friends").upsert...');
                const { data, error } = await supabase
                  .from('friends')
                  .upsert({ user_id: currentUser.id, friend_id: targetId, status: 'active' }, { onConflict: 'user_id,friend_id' })
                  .select()
                  .single();
                console.log('Follow upsert result:', { data, error });
                try { console.log('Follow upsert result (stringified):', JSON.stringify(data, null, 2)); } catch (e) { }

                if (error) {
                  console.error('Follow upsert error:', error);
                  const msg = String(error.message || error?.details || '关注失败');
                  if (/conflict|duplicate|unique|23505|409/i.test(msg) || (error.status === 409)) {
                    try {
                      const { data: existing } = await supabase
                        .from('friends')
                        .select('*')
                        .eq('user_id', currentUser.id)
                        .eq('friend_id', targetId)
                        .maybeSingle();
                      if (existing) {
                        setIsFollowing(true);
                        showSuccess && showSuccess(t('travelPostDetail.messages.followed'));
                        return;
                      }
                    } catch (checkErr) {
                      console.error('Follow existence check failed:', checkErr);
                    }
                    setIsFollowing(true);
                    showSuccess && showSuccess(t('travelPostDetail.messages.followed'));
                    return;
                  }
                  showError && showError(msg);
                  return;
                }

                setIsFollowing(true);
                showSuccess && showSuccess(t('travelPostDetail.messages.followed'));
              } catch (e: any) {
                console.error('Unexpected follow error:', e);
                try { showError && showError(String(e?.message || t('travelPostDetail.messages.followFailed'))); } catch (er) { console.error(er); }
              }
            }}
        />
      )}

      {/* Action Buttons: Clone & Map */}
      {post.source_type === 'itinerary_snapshot' && (
        <View style={{ paddingHorizontal: 16, paddingBottom: 12, flexDirection: 'row', gap: 12 }}>
          {/* Clone Button - Only for real itineraries */}
          {isRealItinerary && (
              <TouchableOpacity
              onPress={handleClone}
              disabled={cloning}
              style={{
                flex: 1,
                backgroundColor: '#FB7A4A',
                borderRadius: 999,
                paddingVertical: 12,
                paddingHorizontal: 18,
                alignItems: 'center',
                justifyContent: 'center',
                flexDirection: 'row',
                gap: 8,
                opacity: cloning ? 0.7 : 1,
                shadowColor: '#FB7A4A',
                shadowOffset: { width: 0, height: 4 },
                shadowOpacity: 0.2,
                shadowRadius: 8,
                elevation: 4,
              }}
            >
              {cloning ? (
                <ActivityIndicator color="#fff" size="small" />
              ) : (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M8 2H16C18.2091 2 20 3.79086 20 6V18C20 20.2091 18.2091 22 16 22H8C5.79086 22 4 20.2091 4 18V6C4 3.79086 5.79086 2 8 2Z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M16 2V6C16 7.10457 15.1046 8 14 8H10C8.89543 8 8 7.10457 8 6V2" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M12 12V17" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M9.5 14.5L12 17L14.5 14.5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              )}
              <Text style={{ color: '#fff', fontWeight: 'bold', fontSize: 16 }}>
                {cloning ? t('travelPostDetail.actions.cloning') : t('travelPostDetail.actions.cloneToItinerary')}
              </Text>
            </TouchableOpacity>
          )}

          {/* Map Button - Show if we have spots OR if it's a real itinerary */}
          {(routeSpots.length > 0 || isRealItinerary) && (
              <TouchableOpacity
              onPress={() => {
                if (routeSpots.length === 0) {
                  showInfo && showInfo(t('travelPostDetail.messages.noMapData') || '暂无地图数据');
                  return;
                }
                setShowRouteMap(true);
              }}
              style={{
                flex: 1,
                backgroundColor: isRealItinerary ? '#fff' : '#FB7A4A',
                borderWidth: isRealItinerary ? 1 : 0,
                borderColor: '#FB7A4A',
                borderRadius: 999,
                paddingVertical: 12,
                paddingHorizontal: 18,
                alignItems: 'center',
                justifyContent: 'center',
                flexDirection: 'row',
                gap: 8,
                shadowColor: isRealItinerary ? 'transparent' : '#FB7A4A',
                shadowOffset: { width: 0, height: 4 },
                shadowOpacity: 0.2,
                shadowRadius: 8,
                elevation: isRealItinerary ? 0 : 4,
              }}
            >
              <MdLocationOn size={20} color={isRealItinerary ? '#FB7A4A' : '#fff'} />
              <Text style={{ color: isRealItinerary ? '#FB7A4A' : '#fff', fontWeight: 'bold', fontSize: 16 }}>
                {t('travelPostDetail.actions.viewMap') || '地图模式'}
              </Text>
            </TouchableOpacity>
          )}
        </View>
      )}

      {/* 元数据信息 */}
      <View style={styles.metaSection}>
        {/* 发布日期和地点 */}
        <View style={styles.metaRow}>
          <Text style={styles.publishDate}>
            {new Date(post.created_at).toLocaleDateString(language === 'zh-CN' ? 'zh-CN' : language === 'zh-TW' ? 'zh-TW' : language === 'ja' ? 'ja-JP' : language === 'ko' ? 'ko-KR' : 'en-US', {
              year: 'numeric',
              month: 'long',
              day: 'numeric',
            })}
          </Text>
          {post.city && (
            <Text style={styles.city}><MdLocationOn size={14} color="#ff6b35" style={{ verticalAlign: 'text-bottom', marginRight: 2 }} /> {post.city}</Text>
          )}
        </View>

        {/* 摘要 */}
        {post.summary && (
          <Text style={styles.summary}>{post.summary}</Text>
        )}
      </View>

      {/* 内容 */}
      <View style={styles.contentContainer}>
        {contentData ? (
          isSnapshot ? (
            <SnapshotContentRenderer 
              contentJson={contentData} 
              authorId={post.author_id} 
              t={t} 
              locale={language}
              onAddToItinerary={handleAddToItinerary}
              onOpenLightbox={handleOpenLightbox}
              onTagPress={(tag) => navigate(`/cards?tags=${encodeURIComponent(tag)}`)}
            />
          ) : (
            <RichContentRenderer 
              contentJson={contentData} 
              t={t} 
              onTagPress={(tag) => navigate(`/cards?tags=${encodeURIComponent(tag)}`)}
            />
          )
        ) : (
          <Text style={styles.emptyContent}>{t('travelPostDetail.emptyContent')}</Text>
        )}
      </View>

      {/* 标签 - 移到最下面 */}
      {post.tags && post.tags.length > 0 && (
        <View style={styles.tagsContainer}>
          <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8 }}>
            {post.tags.map((tag: string, index: number) => (
              <TouchableOpacity 
                key={index}
                onPress={() => navigate(`/search?q=${encodeURIComponent(tag)}`)}
              >
                <Text style={styles.tagLink}>#{tag}</Text>
              </TouchableOpacity>
            ))}
          </View>
        </View>
      )}

      {/* 互动区 - 点赞、浏览、评论、收藏 */}
      <PostEngagement
        post_id={id}
        viewCount={post.view_count || 0}
        likeCount={post.like_count || 0}
        commentCount={actualCommentCount || post.comment_count || 0}
        liked={liked}
        onLike={handleLike}
        comments={[]} // Initial comments are loaded inside component
        postAuthor={author ? {
          id: author.id,
          display_name: author.display_name,
          avatar_url: author.avatar_url
        } : undefined}
        onAddComment={handleCommentAdded}
        onCommentCountChange={(count: number) => setActualCommentCount(count)}
      />

      {/* 底部间距 */}
      <View style={styles.bottomPadding} />
      </View>

      </ScrollView>

      <TouchableOpacity 
        style={[
          styles.backToTopButton, 
          Platform.OS === 'web' && { 
            position: 'fixed', 
            zIndex: 21000,
            opacity: showBackToTop ? 1 : 0,
            pointerEvents: showBackToTop ? 'auto' : 'none',
            transition: 'opacity 0.3s ease-in-out',
          } as any
        ]} 
        onPress={scrollToTop}
        activeOpacity={0.8}
        accessibilityLabel={t('travelPostDetail.actions.backToTop')}
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M4 12l1.41 1.41L11 7.83V20h2V7.83l5.58 5.59L20 12l-8-8-8 8z" fill="white"/>
        </svg>
      </TouchableOpacity>

      {/* Route Map Sheet Overlay */}
      {showRouteMap && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 2000,
          pointerEvents: 'none',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-end',
        }}>
          {/* Backdrop */}
          <div 
            onClick={() => setShowRouteMap(false)}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0,0,0,0.4)',
              pointerEvents: 'auto',
              opacity: showRouteMap ? 1 : 0,
              transition: 'opacity 0.3s',
            }}
          />
          
          {/* Sheet Container */}
          <div
            style={{
              height: `${sheetHeight}%`,
              backgroundColor: '#fff',
              borderTopLeftRadius: 20,
              borderTopRightRadius: 20,
              overflow: 'hidden',
              boxShadow: '0 -8px 24px rgba(0,0,0,0.2)',
              zIndex: 2001,
              transition: isDragging ? 'none' : 'height 0.3s ease',
              display: 'flex',
              flexDirection: 'column',
              pointerEvents: 'auto',
            }}
          >
            {/* Drag Handle */}
            <div 
              onPointerDown={handleSheetPointerDown}
              onTouchStart={handleSheetPointerDown}
              style={{ 
                height: 48, 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center', 
                cursor: isDragging ? 'grabbing' : 'grab',
                touchAction: 'none',
                userSelect: 'none',
                WebkitUserSelect: 'none',
                flexShrink: 0,
                borderBottom: '1px solid #eee'
              }}
            >
              <div style={{ width: 40, height: 5, background: '#d1d5db', borderRadius: 3 }} />
              {/* Close Button */}
              <button 
                onClick={() => setShowRouteMap(false)} 
                style={{ position: 'absolute', right: 16, background: 'transparent', border: 'none', fontSize: 24, cursor: 'pointer', color: '#6B7280' }} 
                aria-label="Close"
              >
                ✕
              </button>
            </div>

            {/* Content */}
            <div style={{ flex: 1, overflow: 'auto', WebkitOverflowScrolling: 'touch' as any }}>
              <RouteMapSheet
                visible={true}
                selectedDay={null}
                routeSpots={routeSpots}
                routeSpotsLoading={false}
                sheetHeight={sheetHeight}
                sheetBottomPadding={80}
                itineraryName={post.title}
                onSelectSpot={(spotId) => {
                  console.log('Selected spot:', spotId);
                }}
                onSelectListItem={(index) => {
                  console.log('Selected item:', index);
                }}
              />
            </div>
          </div>
        </div>
      )}

      <CloneItineraryModal
        visible={showCloneModal}
        onClose={() => setShowCloneModal(false)}
        onConfirm={handleConfirmClone}
        initialTitle={post ? `${t('travelPostDetail.cloneModal.copyOf')} ${post.title}` : ''}
      />

      {/* 加入行程 Modal - 用於從遊記中添加外部項目到自己的行程 */}
      <AddToItineraryModal
        visible={showAddToItineraryModal}
        onClose={() => {
          setShowAddToItineraryModal(false);
          setExternalSharePayload(null);
        }}
        cardSnapshot={null}
        externalShare={externalSharePayload ? {
          title: externalSharePayload.title,
          text: externalSharePayload.text,
          url: externalSharePayload.url,
          imageUrl: externalSharePayload.imageUrl,
          rawPayload: externalSharePayload.rawPayload,
          source: 'manual',
        } : null}
        onAdded={(entry) => {
          showSuccess && showSuccess(t('travelPostDetail.messages.addedToItinerary'));
          setShowAddToItineraryModal(false);
          setExternalSharePayload(null);
        }}
      />

      {/* Lightbox Portal */}
      {lightboxState && typeof document !== 'undefined' && ReactDOM.createPortal(
        <div 
          id="travel-post-lightbox"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            width: '100vw',
            height: '100vh',
            backgroundColor: 'rgba(0, 0, 0, 0.95)',
            zIndex: 2147483647,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            touchAction: 'none',
            overscrollBehavior: 'contain',
          }}
          onClick={closeLightbox}
        >
          <img
            src={lightboxState.images[lightboxState.currentIndex].url}
            alt={lightboxState.images[lightboxState.currentIndex].alt || ''}
            style={{
              maxWidth: '90vw',
              maxHeight: '90vh',
              objectFit: 'contain',
            }}
            onClick={(e) => e.stopPropagation()}
          />
          
            {/* Close Button */}
            <button
              onClick={closeLightbox}
              style={{
                position: 'absolute',
                top: 20,
                right: 20,
                width: 40,
                height: 40,
                borderRadius: '50%',
                border: 'none',
                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                color: '#000',
                fontSize: 20,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 'bold',
                zIndex: 2147483647,
              }}
            >
              <MdClose size={24} />
            </button>
          
          {lightboxState.images.length > 1 && (
            <>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  prevImage();
                }}
                style={{
                  position: 'absolute',
                  left: 20,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  width: 40,
                  height: 40,
                  borderRadius: '50%',
                  border: 'none',
                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                  color: '#000',
                  fontSize: 24,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 'bold',
                }}
              >
                <MdChevronLeft size={30} />
              </button>
              
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  nextImage();
                }}
                style={{
                  position: 'absolute',
                  right: 20,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  width: 40,
                  height: 40,
                  borderRadius: '50%',
                  border: 'none',
                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                  color: '#000',
                  fontSize: 24,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 'bold',
                }}
              >
                <MdChevronRight size={30} />
              </button>
              
              <div style={{
                position: 'absolute',
                bottom: 30,
                left: '50%',
                transform: 'translateX(-50%)',
                backgroundColor: 'rgba(0, 0, 0, 0.6)',
                color: 'white',
                padding: '6px 12px',
                borderRadius: 16,
                fontSize: 14,
                fontWeight: 500,
              }}>
                {lightboxState.currentIndex + 1} / {lightboxState.images.length}
              </div>

              {/* Title / Caption */}
              {lightboxState.images[lightboxState.currentIndex].title && (
                <div style={{
                  position: 'absolute',
                  bottom: 80,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  backgroundColor: 'rgba(0, 0, 0, 0.6)',
                  color: 'white',
                  padding: '8px 16px',
                  borderRadius: 8,
                  fontSize: 15,
                  fontWeight: 500,
                  maxWidth: '90%',
                  textAlign: 'center',
                  backdropFilter: 'blur(4px)',
                  WebkitBackdropFilter: 'blur(4px)',
                }}>
                  {lightboxState.images[lightboxState.currentIndex].title}
                </div>
              )}
            </>
          )}
        </div>,
        document.body
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f6f7f8',
  },
  centerContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'transparent',
    paddingHorizontal: 16,
  },
  loadingText: {
    marginTop: 12,
    fontSize: 14,
    color: '#666',
  },
  errorText: {
    fontSize: 16,
    color: '#ff4444',
    marginBottom: 16,
    textAlign: 'center',
  },
  backLink: {
    fontSize: 14,
    color: '#ff6b35',
    textDecorationLine: 'underline',
  },
  coverImage: {
    width: '100%',
    aspectRatio: 16 / 9,
    backgroundColor: '#f5f5f5',
  },
  metaSection: {
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#f0f0f0',
  },
  metaRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    marginBottom: 8,
  },
  publishDate: {
    fontSize: 12,
    color: '#999',
  },
  city: {
    fontSize: 12,
    color: '#ff6b35',
    fontWeight: '500',
  },
  summary: {
    fontSize: 14,
    color: '#666',
    lineHeight: 20,
  },
  tagsContainer: {
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderTopWidth: 1,
    borderTopColor: '#f0f0f0',
  },
  tagsText: {
    fontSize: 13,
    color: '#666',
    lineHeight: 20,
  },
  tagLink: {
    fontSize: 13,
    color: '#0ea5a4',
    lineHeight: 20,
    fontWeight: '500',
  },
  contentContainer: {
    paddingHorizontal: 16,
    paddingVertical: 20,
  },
  emptyContent: {
    fontSize: 14,
    color: '#999',
    textAlign: 'center',
    paddingVertical: 32,
  },
  bottomPadding: {
    height: 32,
  },
  // cover wrapper for overlaying back button
  coverWrapper: {
    position: 'relative' as any,
  },
  coverOverlay: {
    position: 'absolute' as any,
    left: 0,
    right: 0,
    bottom: 0,
    height: '60%',
    background: 'linear-gradient(to top, rgba(0,0,0,0.7), rgba(0,0,0,0))' as any,
    zIndex: 10,
  },
  titleWrapper: {
    position: 'absolute' as any,
    left: 0,
    right: 0,
    bottom: 0,
    paddingHorizontal: 16,
    paddingVertical: 20,
    zIndex: 20,
  },
  coverTitle: {
    fontSize: 24,
    fontWeight: 'bold' as any,
    color: '#ffffff',
    textShadowColor: 'rgba(0, 0, 0, 0.3)' as any,
    textShadowOffset: { width: 0, height: 1 } as any,
    textShadowRadius: 3,
    lineHeight: 32,
  },
  backWrap: {
    position: 'absolute' as any,
    left: 6,
    top: 12,
    zIndex: 60,
  },
  backText: {
    color: '#fff',
    backgroundColor: 'rgba(0,0,0,0.45)',
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 6,
    fontSize: 14,
  },
  backToTopButton: {
    position: 'absolute',
    right: 16,
    bottom: 200, // Increased to avoid blocking comment input
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: '#FF6B35',
    justifyContent: 'center',
    alignItems: 'center',
    elevation: 5,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.25,
    shadowRadius: 3.84,
    zIndex: 100,
    cursor: 'pointer',
  },
  contentWrapper: {
    maxWidth: 600,
    width: '100%',
    alignSelf: 'center',
    backgroundColor: '#ffffff',
    borderRadius: 8,
    boxShadow: '0 6px 18px rgba(16,24,40,0.06)' as any,
    overflow: 'hidden' as any,
  },
  // Sticky Header
  stickyHeader: {
    position: 'fixed' as any,
    top: 0,
    left: 0,
    right: 0,
    height: 56,
    backgroundColor: '#fff',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    zIndex: 1000,
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB',
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)' as any,
  },
  stickyBackBtn: {
    padding: 8,
    marginLeft: -8,
    cursor: 'pointer' as any,
  },
  stickyTitle: {
    flex: 1,
    fontSize: 16,
    fontWeight: '600' as any,
    color: '#111827',
    textAlign: 'center',
    marginHorizontal: 16,
    overflow: 'hidden' as any,
    textOverflow: 'ellipsis' as any,
    whiteSpace: 'nowrap' as any,
  },
});

export default TravelPostDetailPage;
